import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchPersonelDurumlari,
  fetchAtanabilirKullanicilar,
  fetchDahililer,
  createDahili,
  updateDahili,
  deleteDahili,
  ataDahili,
  fetchKuyruklar,
  createKuyruk,
  updateKuyruk,
  deleteKuyruk,
} from '../services/santralYonetimService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import CagriMerkeziDashboard from '../components/CagriMerkeziDashboard';
import './MusteriHizmetleriPage.css';

const notify = (type, msg) => {
  if (window.showNotification) window.showNotification(type, msg);
  else if (type === 'error') console.error(msg);
};

/** Çalışma penceresi (timeline ekseni) — varsayılan 08:00-20:00 */
const CALISMA_BASLANGIC_SAAT = 8;
const CALISMA_BITIS_SAAT = 20;

const DURUM_META = {
  musait: { label: 'Müsait', renk: '#16a34a' },
  mesgul: { label: 'Meşgul', renk: '#f59e0b' },
  gorusmede: { label: 'Görüşmede', renk: '#2563eb' },
  cevrimdisi: { label: 'Çevrimdışı', renk: '#9ca3af' },
};

const POLL_MS = 20000;

const bugunStr = () => {
  const d = new Date();
  const ay = String(d.getMonth() + 1).padStart(2, '0');
  const gun = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${ay}-${gun}`;
};

const sureFormat = (sn) => {
  const s = Math.max(0, Math.round(sn || 0));
  const saat = Math.floor(s / 3600);
  const dk = Math.floor((s % 3600) / 60);
  if (saat > 0) return `${saat}s ${dk}dk`;
  return `${dk}dk`;
};

const saatDkMs = (ms) =>
  new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

/** ms → "SS:DD:SS" (saat:dakika:saniye) tam saatli zaman damgası */
const saatDkSnMs = (ms) =>
  new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Saniye süresini "00:48:13" biçimine çevirir */
const sureUzun = (sn) => {
  const s = Math.max(0, Math.floor(sn || 0));
  const h = Math.floor(s / 3600);
  const d = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(d).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

/** Seçili tarih için çalışma penceresi sınırları (ms) */
const pencereSinirlari = (tarihStr) => {
  const [y, m, d] = (tarihStr || bugunStr()).split('-').map(Number);
  const bas = new Date(y, m - 1, d, CALISMA_BASLANGIC_SAAT, 0, 0, 0).getTime();
  const bit = new Date(y, m - 1, d, CALISMA_BITIS_SAAT, 0, 0, 0).getTime();
  return { bas, bit };
};

/** Pencere içinde "şimdiye kadar" sayılacak bitiş (bugün=şu an, geçmiş=tam pencere, gelecek=yok) */
const etkinBitis = (tarihStr, bas, bit) => {
  const [y, m, d] = (tarihStr || bugunStr()).split('-').map(Number);
  const gunBas = new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);
  if (gunBas > bugun.getTime()) return bas; // gelecek gün -> doldurma yok
  if (gunBas === bugun.getTime()) return Math.min(Date.now(), bit); // bugün -> şu ana kadar
  return bit; // geçmiş gün -> tüm pencere
};

/**
 * Kayıtlı segmentleri pencereye kırpar ve boşlukları çevrimdışı ile doldurur.
 * Böylece hiç çevrimiçi olmamış personel için çevrimdışı süresi de hesaplanır.
 * Devam eden (son/canlı) segment `devam: true` ile işaretlenir.
 */
const segmentleriDoldur = (segmentler, tarihStr) => {
  const { bas, bit } = pencereSinirlari(tarihStr);
  const son = etkinBitis(tarihStr, bas, bit);

  const kayitli = (segmentler || [])
    .map((s) => ({
      durum: s.durum,
      sebep: s.sebep || '',
      bas: Math.max(new Date(s.baslangic).getTime(), bas),
      bit: Math.min(new Date(s.bitis).getTime(), son),
    }))
    .filter((s) => s.bit > s.bas)
    .sort((a, b) => a.bas - b.bas);

  const sonuc = [];
  let imlec = bas;
  for (const s of kayitli) {
    if (s.bas > imlec) {
      sonuc.push({ durum: 'cevrimdisi', sebep: '', bas: imlec, bit: s.bas });
    }
    sonuc.push(s);
    imlec = Math.max(imlec, s.bit);
  }
  if (imlec < son) {
    sonuc.push({ durum: 'cevrimdisi', sebep: '', bas: imlec, bit: son });
  }

  // Bugün ise (son = şu an) son segment hâlâ devam ediyor demektir
  if (son < bit && sonuc.length > 0) {
    sonuc[sonuc.length - 1].devam = true;
  }
  return sonuc;
};

/** Doldurulmuş segmentlerden durum başına toplam saniye */
const toplamlarHesapla = (dilimler) => {
  const t = { musait: 0, mesgul: 0, gorusmede: 0, cevrimdisi: 0 };
  for (const s of dilimler) {
    t[s.durum] = (t[s.durum] || 0) + (s.bit - s.bas) / 1000;
  }
  return t;
};

/** Ad-soyaddan baş harfleri üretir (avatar için) */
const basHarfler = (ad) => {
  const parcalar = String(ad || '').trim().split(/\s+/).filter(Boolean);
  if (parcalar.length === 0) return '?';
  if (parcalar.length === 1) return parcalar[0].slice(0, 2).toUpperCase();
  return (parcalar[0][0] + parcalar[parcalar.length - 1][0]).toUpperCase();
};

const emptyDahiliForm = () => ({
  numara: '',
  sipUsername: '',
  sipPassword: '',
  displayName: '',
  queue: '',
  aktif: true,
});

const emptyKuyrukForm = () => ({
  ad: '',
  numara: '',
  aciklama: '',
  ustlenebilir: true,
  aktif: true,
});

const MIN_GORUNUM_MS = 60 * 1000; // En fazla yakınlaşma: 1 dakikalık pencere
const TIK_ADIMLARI_MS = [
  60000, 2 * 60000, 5 * 60000, 10 * 60000, 15 * 60000, 30 * 60000,
  60 * 60000, 2 * 60 * 60000, 3 * 60 * 60000,
];

/** Görünüm aralığına göre uygun eksen tik adımı (yaklaşık 6 tik) */
const tikAdimi = (gorunumMs) => {
  const hedef = gorunumMs / 6;
  return TIK_ADIMLARI_MS.find((c) => c >= hedef) || TIK_ADIMLARI_MS[TIK_ADIMLARI_MS.length - 1];
};

/**
 * İnteraktif timeline:
 *  - Fare tekerleği ile imleç etrafında zoom-in/zoom-out
 *  - Sürükleyerek (drag) sağa/sola pan (yatay scrollbar yok)
 *  - Segment üzerine gelince başlangıç/bitiş/süre/sebep tooltip'i (canlı son parça dahil)
 */
function TimelineBar({ dilimlerHam, tarihStr }) {
  const { bas: pencereBaslangic, bit: pencereBitis } = pencereSinirlari(tarihStr);
  const tamSpan = pencereBitis - pencereBaslangic;

  const trackRef = useRef(null);
  const [gorunum, setGorunum] = useState({ bas: pencereBaslangic, bit: pencereBitis });
  const [tooltip, setTooltip] = useState(null);
  const dragRef = useRef(null);

  // Tarih değişince görünümü tam pencereye sıfırla
  useEffect(() => {
    setGorunum({ bas: pencereBaslangic, bit: pencereBitis });
    setTooltip(null);
  }, [pencereBaslangic, pencereBitis]);

  const gorunumSpan = gorunum.bit - gorunum.bas;

  const clampGorunum = useCallback((bas, bit) => {
    let span = bit - bas;
    if (span < MIN_GORUNUM_MS) span = MIN_GORUNUM_MS;
    if (span > tamSpan) span = tamSpan;
    let yeniBas = bas;
    if (yeniBas < pencereBaslangic) yeniBas = pencereBaslangic;
    if (yeniBas + span > pencereBitis) yeniBas = pencereBitis - span;
    return { bas: yeniBas, bit: yeniBas + span };
  }, [pencereBaslangic, pencereBitis, tamSpan]);

  // Tekerlek (zoom): passive olmayan native listener -> preventDefault güvenli
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = track.getBoundingClientRect();
      const oran = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      setGorunum((g) => {
        const span = g.bit - g.bas;
        const imlecZaman = g.bas + oran * span;
        const faktor = e.deltaY < 0 ? 0.8 : 1.25; // yukarı: yakınlaş, aşağı: uzaklaş
        let yeniSpan = Math.min(tamSpan, Math.max(MIN_GORUNUM_MS, span * faktor));
        const yeniBas = imlecZaman - oran * yeniSpan;
        return clampGorunum(yeniBas, yeniBas + yeniSpan);
      });
    };
    track.addEventListener('wheel', onWheel, { passive: false });
    return () => track.removeEventListener('wheel', onWheel);
  }, [tamSpan, clampGorunum]);

  // Sürükleyerek pan
  const handleMouseDown = useCallback((e) => {
    dragRef.current = { x: e.clientX, bas: gorunum.bas, moved: false };
  }, [gorunum.bas]);

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const dx = e.clientX - drag.x;
      if (Math.abs(dx) > 2) drag.moved = true;
      const dzaman = -(dx / rect.width) * gorunumSpan;
      setGorunum(clampGorunum(drag.bas + dzaman, drag.bas + dzaman + gorunumSpan));
    };
    const onUp = () => {
      if (dragRef.current) {
        const moved = dragRef.current.moved;
        dragRef.current = null;
        // Sürükleme bittiğinde kısa süre tooltip'i bastır (tıklama/hover karışmasın)
        if (moved) setTooltip(null);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [gorunumSpan, clampGorunum]);

  const segTooltip = useCallback((e, s) => {
    if (dragRef.current?.moved) return;
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const canliBit = s.devam ? Date.now() : s.bit;
    const sureSn = (canliBit - s.bas) / 1000;
    setTooltip({
      x: e.clientX - rect.left,
      durum: s.durum,
      bas: s.bas,
      bit: canliBit,
      devam: !!s.devam,
      sebep: s.sebep || '',
      sure: sureUzun(sureSn),
    });
  }, []);

  const dilimler = (dilimlerHam || [])
    .map((s, i) => {
      const bas = Math.max(s.bas, gorunum.bas);
      const bit = Math.min(s.bit, gorunum.bit);
      if (bit <= bas) return null;
      return {
        key: i,
        ham: s,
        left: ((bas - gorunum.bas) / gorunumSpan) * 100,
        width: ((bit - bas) / gorunumSpan) * 100,
      };
    })
    .filter(Boolean);

  // Eksen tikleri (görünür aralık içinde)
  const adim = tikAdimi(gorunumSpan);
  const tikler = [];
  const ilkTik = Math.ceil(gorunum.bas / adim) * adim;
  for (let t = ilkTik; t <= gorunum.bit; t += adim) {
    tikler.push({
      ms: t,
      left: ((t - gorunum.bas) / gorunumSpan) * 100,
      etiket: saatDkMs(t),
    });
  }

  const simdi = Date.now();
  const simdiYuzde =
    simdi >= gorunum.bas && simdi <= gorunum.bit
      ? ((simdi - gorunum.bas) / gorunumSpan) * 100
      : null;

  const yakinlasmis = gorunumSpan < tamSpan - 1000;

  return (
    <div className="mh-timeline">
      <div
        className="mh-timeline-track"
        ref={trackRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={() => setTooltip(null)}
        role="presentation"
      >
        {dilimler.map((d) => (
          <div
            key={d.key}
            className={`mh-timeline-seg${d.ham.devam ? ' mh-timeline-seg--devam' : ''}`}
            style={{ left: `${d.left}%`, width: `${d.width}%`, background: DURUM_META[d.ham.durum]?.renk }}
            onMouseEnter={(e) => segTooltip(e, d.ham)}
            onMouseMove={(e) => segTooltip(e, d.ham)}
          />
        ))}
        {simdiYuzde !== null ? (
          <div className="mh-timeline-now" style={{ left: `${simdiYuzde}%` }} title="Şu an" />
        ) : null}
      </div>

      {tooltip ? (
        <div
          className="mh-tip"
          style={{ left: `${Math.min(Math.max(tooltip.x, 95), (trackRef.current?.clientWidth || 300) - 95)}px` }}
        >
          <div className="mh-tip-row mh-tip-head">
            <span className="mh-tip-dot" style={{ background: DURUM_META[tooltip.durum]?.renk }} />
            {DURUM_META[tooltip.durum]?.label || tooltip.durum}
            {tooltip.devam ? <span className="mh-tip-live">canlı</span> : null}
          </div>
          <div className="mh-tip-row">
            <span className="mh-tip-label">Başlangıç</span>
            <span className="mh-tip-val">{saatDkSnMs(tooltip.bas)}</span>
          </div>
          <div className="mh-tip-row">
            <span className="mh-tip-label">Bitiş</span>
            <span className="mh-tip-val">{tooltip.devam ? '— (devam ediyor)' : saatDkSnMs(tooltip.bit)}</span>
          </div>
          <div className="mh-tip-row">
            <span className="mh-tip-label">Süre</span>
            <span className="mh-tip-val mh-tip-sure">{tooltip.sure}</span>
          </div>
          {tooltip.durum === 'mesgul' ? (
            <div className="mh-tip-row">
              <span className="mh-tip-label">Sebep</span>
              <span className="mh-tip-val">{tooltip.sebep || '-'}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mh-timeline-axis">
        {tikler.map((t) => (
          <span key={t.ms} className="mh-timeline-tick" style={{ left: `${t.left}%` }}>
            {t.etiket}
          </span>
        ))}
      </div>

      <div className="mh-timeline-hint">
        <i className="fas fa-arrows-left-right" aria-hidden /> Sürükleyerek kaydır ·{' '}
        <i className="fas fa-magnifying-glass" aria-hidden /> tekerlekle yakınlaştır
        {yakinlasmis ? (
          <button
            type="button"
            className="mh-timeline-reset"
            onClick={() => setGorunum({ bas: pencereBaslangic, bit: pencereBitis })}
          >
            <i className="fas fa-compress" aria-hidden /> Tam görünüm
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function MusteriHizmetleriPage() {
  const { confirm } = useConfirmDialog();
  const [sekme, setSekme] = useState('cagri-merkezi');

  // --- Sekme 1: Personel durumları ---
  const [tarih, setTarih] = useState(bugunStr());
  const [personeller, setPersoneller] = useState([]);
  const [durumYukleniyor, setDurumYukleniyor] = useState(false);
  const [durumHata, setDurumHata] = useState('');
  const pollRef = useRef(null);

  const durumlariGetir = useCallback(async (sessiz = false) => {
    if (!sessiz) setDurumYukleniyor(true);
    setDurumHata('');
    try {
      const r = await fetchPersonelDurumlari(tarih);
      setPersoneller(r?.veri?.personeller || []);
    } catch (e) {
      setDurumHata(e?.message || 'Personel durumları alınamadı');
    } finally {
      if (!sessiz) setDurumYukleniyor(false);
    }
  }, [tarih]);

  useEffect(() => {
    if (sekme !== 'durumlar') return undefined;
    durumlariGetir();
    pollRef.current = setInterval(() => durumlariGetir(true), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [sekme, durumlariGetir]);

  // --- Sekme 2: Dahililer ---
  const [dahililer, setDahililer] = useState([]);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [dahiliYukleniyor, setDahiliYukleniyor] = useState(false);
  const [dahiliHata, setDahiliHata] = useState('');
  const [form, setForm] = useState(emptyDahiliForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const dahililerGetir = useCallback(async () => {
    setDahiliYukleniyor(true);
    setDahiliHata('');
    try {
      const [d, k] = await Promise.all([fetchDahililer(), fetchAtanabilirKullanicilar()]);
      setDahililer(d?.veri?.dahililer || []);
      setKullanicilar(k?.veri?.kullanicilar || []);
    } catch (e) {
      setDahiliHata(e?.message || 'Dahililer alınamadı');
    } finally {
      setDahiliYukleniyor(false);
    }
  }, []);

  // --- Sekme 3: Kuyruklar ---
  const [kuyruklar, setKuyruklar] = useState([]);
  const [kuyrukYukleniyor, setKuyrukYukleniyor] = useState(false);
  const [kuyrukHata, setKuyrukHata] = useState('');
  const [kuyrukForm, setKuyrukForm] = useState(emptyKuyrukForm());
  const [kuyrukEditingId, setKuyrukEditingId] = useState(null);
  const [kuyrukSaving, setKuyrukSaving] = useState(false);

  const kuyruklarGetir = useCallback(async () => {
    setKuyrukYukleniyor(true);
    setKuyrukHata('');
    try {
      const r = await fetchKuyruklar();
      setKuyruklar(r?.veri?.kuyruklar || []);
    } catch (e) {
      setKuyrukHata(e?.message || 'Kuyruklar alınamadı');
    } finally {
      setKuyrukYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    if (sekme !== 'dahililer') return;
    dahililerGetir();
    kuyruklarGetir();
  }, [sekme, dahililerGetir, kuyruklarGetir]);

  useEffect(() => {
    if (sekme !== 'kuyruklar') return;
    kuyruklarGetir();
  }, [sekme, kuyruklarGetir]);

  const kuyrukFormResetle = () => {
    setKuyrukEditingId(null);
    setKuyrukForm(emptyKuyrukForm());
  };

  const kuyrukDuzenleBasla = (k) => {
    setKuyrukEditingId(k.id || k._id);
    setKuyrukForm({
      ad: k.ad || '',
      numara: k.numara || '',
      aciklama: k.aciklama || '',
      ustlenebilir: k.ustlenebilir !== false,
      aktif: k.aktif !== false,
    });
  };

  const kuyrukFormGonder = async (e) => {
    e.preventDefault();
    setKuyrukSaving(true);
    setKuyrukHata('');
    try {
      const payload = {
        ad: kuyrukForm.ad.trim(),
        numara: kuyrukForm.numara.trim(),
        aciklama: kuyrukForm.aciklama.trim(),
        ustlenebilir: !!kuyrukForm.ustlenebilir,
        aktif: !!kuyrukForm.aktif,
      };
      if (!payload.ad || !payload.numara) {
        setKuyrukHata('Kuyruk adı ve numarası zorunludur.');
        setKuyrukSaving(false);
        return;
      }
      if (kuyrukEditingId) {
        const r = await updateKuyruk(kuyrukEditingId, payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Güncellenemedi');
      } else {
        const r = await createKuyruk(payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Oluşturulamadı');
      }
      notify('success', kuyrukEditingId ? 'Kuyruk güncellendi' : 'Kuyruk eklendi');
      kuyrukFormResetle();
      await kuyruklarGetir();
    } catch (err) {
      setKuyrukHata(err?.message || 'İşlem başarısız');
    } finally {
      setKuyrukSaving(false);
    }
  };

  const kuyrukSilme = async (k) => {
    const id = k.id || k._id;
    const ok = await confirm({
      title: 'Kuyruğu sil',
      message: `"${k.ad}" kuyruğunu silmek istediğinize emin misiniz?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    try {
      const r = await deleteKuyruk(id);
      if (!r?.basarili) throw new Error(r?.mesaj || 'Silinemedi');
      notify('success', 'Kuyruk silindi');
      if (kuyrukEditingId === id) kuyrukFormResetle();
      await kuyruklarGetir();
    } catch (err) {
      notify('error', err?.message || 'Silinemedi');
    }
  };

  const formResetle = () => {
    setEditingId(null);
    setForm(emptyDahiliForm());
  };

  const duzenleBasla = (d) => {
    setEditingId(d.id || d._id);
    setForm({
      numara: d.numara || '',
      sipUsername: d.sipUsername || '',
      sipPassword: '',
      displayName: d.displayName || '',
      queue: d.queue || '',
      aktif: d.aktif !== false,
    });
  };

  const formGonder = async (e) => {
    e.preventDefault();
    setSaving(true);
    setDahiliHata('');
    try {
      const payload = {
        numara: form.numara.trim(),
        sipUsername: form.sipUsername.trim(),
        displayName: form.displayName.trim(),
        queue: form.queue.trim(),
        aktif: !!form.aktif,
      };
      if (form.sipPassword.trim() !== '') payload.sipPassword = form.sipPassword;

      if (!payload.numara || !payload.sipUsername) {
        setDahiliHata('Dahili numarası ve SIP kullanıcı adı zorunludur.');
        setSaving(false);
        return;
      }

      if (editingId) {
        const r = await updateDahili(editingId, payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Güncellenemedi');
      } else {
        if (!payload.sipPassword) {
          setDahiliHata('Yeni dahili için SIP şifresi zorunludur.');
          setSaving(false);
          return;
        }
        const r = await createDahili(payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Oluşturulamadı');
      }
      notify('success', editingId ? 'Dahili güncellendi' : 'Dahili eklendi');
      formResetle();
      await dahililerGetir();
    } catch (err) {
      setDahiliHata(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const dahiliSilme = async (d) => {
    const id = d.id || d._id;
    const ok = await confirm({
      title: 'Dahiliyi sil',
      message: `"${d.numara}" dahilisini silmek istediğinize emin misiniz?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    try {
      const r = await deleteDahili(id);
      if (!r?.basarili) throw new Error(r?.mesaj || 'Silinemedi');
      notify('success', 'Dahili silindi');
      if (editingId === id) formResetle();
      await dahililerGetir();
    } catch (err) {
      notify('error', err?.message || 'Silinemedi');
    }
  };

  const atamaDegistir = async (d, kullaniciId) => {
    try {
      const r = await ataDahili(d.id || d._id, kullaniciId);
      if (!r?.basarili) throw new Error(r?.mesaj || 'Atama başarısız');
      notify('success', kullaniciId ? 'Dahili atandı' : 'Atama kaldırıldı');
      await dahililerGetir();
    } catch (err) {
      notify('error', err?.message || 'Atama başarısız');
    }
  };

  return (
    <div className="mh-page">
      <div className="mh-header">
        <div>
          <h1 className="mh-title">
            <i className="fas fa-headset" aria-hidden /> Müşteri Hizmetleri
          </h1>
        </div>
      </div>

      <div className="mh-tabs">
        <button
          type="button"
          className={`mh-tab${sekme === 'cagri-merkezi' ? ' is-active' : ''}`}
          onClick={() => setSekme('cagri-merkezi')}
        >
          <i className="fas fa-chart-line" aria-hidden /> Çağrı Merkezi
        </button>
        <button
          type="button"
          className={`mh-tab${sekme === 'durumlar' ? ' is-active' : ''}`}
          onClick={() => setSekme('durumlar')}
        >
          <i className="fas fa-chart-gantt" aria-hidden /> Personel Durumları
        </button>
        <button
          type="button"
          className={`mh-tab${sekme === 'dahililer' ? ' is-active' : ''}`}
          onClick={() => setSekme('dahililer')}
        >
          <i className="fas fa-phone-volume" aria-hidden /> Dahililer
        </button>
        <button
          type="button"
          className={`mh-tab${sekme === 'kuyruklar' ? ' is-active' : ''}`}
          onClick={() => setSekme('kuyruklar')}
        >
          <i className="fas fa-layer-group" aria-hidden /> Kuyruklar
        </button>
      </div>

      {sekme === 'cagri-merkezi' ? (
        <div className="mh-panel">
          <CagriMerkeziDashboard />
        </div>
      ) : null}

      {sekme === 'durumlar' ? (
        <div className="mh-panel">
          <div className="mh-toolbar">
            <label className="mh-date-field">
              <span>Tarih</span>
              <input
                type="date"
                value={tarih}
                max={bugunStr()}
                onChange={(e) => setTarih(e.target.value)}
              />
            </label>
            <button type="button" className="mh-refresh-btn" onClick={() => durumlariGetir()}>
              <i className={`fas fa-sync-alt${durumYukleniyor ? ' fa-spin' : ''}`} aria-hidden /> Yenile
            </button>
            <div className="mh-legend">
              {Object.entries(DURUM_META).map(([k, v]) => (
                <span key={k} className="mh-legend-item">
                  <span className="mh-legend-dot" style={{ background: v.renk }} /> {v.label}
                </span>
              ))}
            </div>
          </div>

          {durumHata ? (
            <div className="mh-error"><i className="fas fa-exclamation-circle" aria-hidden /> {durumHata}</div>
          ) : null}

          {durumYukleniyor && personeller.length === 0 ? (
            <div className="mh-loading"><i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…</div>
          ) : personeller.length === 0 ? (
            <div className="mh-empty">
              <i className="fas fa-user-slash" aria-hidden />
              <p>Atanmış dahilisi olan müşteri hizmetleri personeli bulunamadı.</p>
              <p className="mh-empty-hint">Dahililer sekmesinden bir personele dahili atayın.</p>
            </div>
          ) : (
            <div className="mh-personel-list">
              {personeller.map((p) => {
                const cevrimici = p.anlikDurum && p.anlikDurum !== 'cevrimdisi';
                const dilimler = segmentleriDoldur(p.segmentler, tarih);
                const toplamlar = toplamlarHesapla(dilimler);
                return (
                  <div key={p.kullaniciId} className="mh-personel-card">
                    <div className="mh-personel-head">
                      <div className="mh-personel-ident">
                        <span
                          className={`mh-avatar mh-avatar--${p.anlikDurum || 'cevrimdisi'}`}
                          style={{ '--mh-avatar-renk': DURUM_META[p.anlikDurum]?.renk || '#9ca3af' }}
                        >
                          {basHarfler(p.ad)}
                          <span className={`mh-avatar-dot${cevrimici ? ' mh-avatar-dot--online' : ''}`} />
                        </span>
                        <div className="mh-personel-meta">
                          <span className="mh-personel-ad">{p.ad}</span>
                          <span className="mh-personel-dahili">
                            <i className="fas fa-phone" aria-hidden /> Dahili {p.dahiliNumara}
                          </span>
                        </div>
                      </div>
                      <span
                        className="mh-durum-badge"
                        style={{ background: DURUM_META[p.anlikDurum]?.renk || '#9ca3af' }}
                      >
                        <span className="mh-durum-badge-dot" />
                        {DURUM_META[p.anlikDurum]?.label || p.anlikDurum}
                        {p.anlikDurum === 'mesgul' && p.anlikSebep ? ` · ${p.anlikSebep}` : ''}
                      </span>
                    </div>

                    <TimelineBar dilimlerHam={dilimler} tarihStr={tarih} />

                    <div className="mh-totals">
                      {['musait', 'gorusmede', 'mesgul', 'cevrimdisi'].map((k) => (
                        <div key={k} className="mh-total-item" style={{ '--mh-total-renk': DURUM_META[k].renk }}>
                          <span className="mh-total-label">
                            <span className="mh-total-dot" style={{ background: DURUM_META[k].renk }} />
                            {DURUM_META[k].label}
                          </span>
                          <span className="mh-total-value">{sureFormat(toplamlar[k])}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {sekme === 'dahililer' ? (
        <div className="mh-panel">
          <form className="mh-dahili-form" onSubmit={formGonder}>
            <div className="mh-form-grid">
              <label className="mh-field">
                <span>Dahili No *</span>
                <input
                  type="text"
                  value={form.numara}
                  onChange={(e) => setForm((p) => ({ ...p, numara: e.target.value }))}
                  maxLength={16}
                  placeholder="101"
                />
              </label>
              <label className="mh-field">
                <span>SIP Kullanıcı Adı *</span>
                <input
                  type="text"
                  value={form.sipUsername}
                  onChange={(e) => setForm((p) => ({ ...p, sipUsername: e.target.value }))}
                  placeholder="101-8503083532"
                />
              </label>
              <label className="mh-field">
                <span>SIP Şifre {editingId ? '(boş bırak = değişme)' : '*'}</span>
                <input
                  type="password"
                  value={form.sipPassword}
                  onChange={(e) => setForm((p) => ({ ...p, sipPassword: e.target.value }))}
                  autoComplete="new-password"
                  placeholder={editingId ? '••••••' : 'SIP şifresi'}
                />
              </label>
              <label className="mh-field">
                <span>Görünen Ad</span>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
                  placeholder="Örn. Destek 1"
                />
              </label>
              <label className="mh-field">
                <span>Kuyruk (Queue)</span>
                <select
                  value={form.queue}
                  onChange={(e) => setForm((p) => ({ ...p, queue: e.target.value }))}
                >
                  <option value="">— Seçin —</option>
                  {kuyruklar.filter((k) => k.aktif !== false).map((k) => (
                    <option key={k.id || k._id} value={k.ad}>
                      {k.numara} — {k.ad}
                    </option>
                  ))}
                </select>
                <span className="mh-field-hint">
                  Kalıcı kuyruk (meşgul/müsait kontrolü). Yönetim: YONETIM. OPERATOR yalnızca panelden geçici üstlenme ile kullanılır.
                </span>
              </label>
              <label className="mh-field mh-field--checkbox">
                <input
                  type="checkbox"
                  checked={form.aktif}
                  onChange={(e) => setForm((p) => ({ ...p, aktif: e.target.checked }))}
                />
                <span>Aktif</span>
              </label>
            </div>

            {dahiliHata ? (
              <div className="mh-error"><i className="fas fa-exclamation-circle" aria-hidden /> {dahiliHata}</div>
            ) : null}

            <div className="mh-form-actions">
              {editingId ? (
                <button type="button" className="mh-btn-secondary" onClick={formResetle}>
                  Yeni dahili
                </button>
              ) : null}
              <button type="submit" className="mh-btn-primary" disabled={saving}>
                <i className={`fas fa-${saving ? 'spinner fa-spin' : 'save'}`} aria-hidden />
                {saving ? ' Kaydediliyor…' : editingId ? ' Güncelle' : ' Ekle'}
              </button>
            </div>
          </form>

          {dahiliYukleniyor ? (
            <div className="mh-loading"><i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…</div>
          ) : (
            <div className="mh-table-wrap">
              <table className="admins-table mh-table">
                <thead>
                  <tr>
                    <th>Dahili</th>
                    <th>SIP Kullanıcı</th>
                    <th>Görünen Ad</th>
                    <th>Kuyruk</th>
                    <th>Aktif</th>
                    <th>Atanan Personel</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {dahililer.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="mh-table-empty">Henüz dahili tanımlanmamış.</td>
                    </tr>
                  ) : (
                    dahililer.map((d) => (
                      <tr key={d.id || d._id}>
                        <td><strong>{d.numara}</strong></td>
                        <td>{d.sipUsername}</td>
                        <td>{d.displayName || '—'}</td>
                        <td>{d.queue || '—'}</td>
                        <td>{d.aktif !== false ? 'Evet' : 'Hayır'}</td>
                        <td>
                          <select
                            className="mh-ata-select"
                            value={d.atananKullanici || ''}
                            onChange={(e) => atamaDegistir(d, e.target.value)}
                          >
                            <option value="">— Atanmamış —</option>
                            {kullanicilar.map((k) => (
                              <option key={k.id} value={k.id}>
                                {k.ad} ({k.rol})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="table-actions islemler-sutunu">
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => duzenleBasla(d)}
                            title="Düzenle"
                          >
                            <i className="fas fa-edit" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() => dahiliSilme(d)}
                            title="Sil"
                          >
                            <i className="fas fa-trash-alt" aria-hidden />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {sekme === 'kuyruklar' ? (
        <div className="mh-panel">
          <form className="mh-dahili-form" onSubmit={kuyrukFormGonder}>
            <div className="mh-form-grid">
              <label className="mh-field">
                <span>Kuyruk Adı *</span>
                <input
                  type="text"
                  value={kuyrukForm.ad}
                  onChange={(e) => setKuyrukForm((p) => ({ ...p, ad: e.target.value }))}
                  placeholder="OPERATOR veya 85030XXX-queue-OPERATOR"
                />
                <small className="mh-field-hint">Netgsm Netsantral&apos;daki departman adı. OPERATOR: geçici üstlenme; sıradaki çağrı alınır.</small>
              </label>
              <label className="mh-field">
                <span>Kuyruk No *</span>
                <input
                  type="text"
                  value={kuyrukForm.numara}
                  onChange={(e) => setKuyrukForm((p) => ({ ...p, numara: e.target.value }))}
                  placeholder="1"
                />
                <small className="mh-field-hint">Referans numarası (ID gibi)</small>
              </label>
              <label className="mh-field mh-field--wide">
                <span>Açıklama</span>
                <input
                  type="text"
                  value={kuyrukForm.aciklama}
                  onChange={(e) => setKuyrukForm((p) => ({ ...p, aciklama: e.target.value }))}
                  placeholder="Örn. Müşteri hizmetleri kuyruğu"
                />
              </label>
              <label className="mh-field mh-field--checkbox">
                <input
                  type="checkbox"
                  checked={kuyrukForm.ustlenebilir}
                  onChange={(e) => setKuyrukForm((p) => ({ ...p, ustlenebilir: e.target.checked }))}
                />
                <span>Üstlenebilir (panelden cevapla)</span>
                <small className="mh-field-hint">
                  OPERATOR gibi üstlenebilir kuyruklarda panel yalnızca sıradaki arayanı alır.
                  NetGSM API belirli arayan seçimini desteklemez (agentlogin → FIFO).
                </small>
              </label>
              <label className="mh-field mh-field--checkbox">
                <input
                  type="checkbox"
                  checked={kuyrukForm.aktif}
                  onChange={(e) => setKuyrukForm((p) => ({ ...p, aktif: e.target.checked }))}
                />
                <span>Aktif</span>
              </label>
            </div>

            {kuyrukHata ? (
              <div className="mh-error"><i className="fas fa-exclamation-circle" aria-hidden /> {kuyrukHata}</div>
            ) : null}

            <div className="mh-form-actions">
              {kuyrukEditingId ? (
                <button type="button" className="mh-btn-secondary" onClick={kuyrukFormResetle}>
                  Yeni kuyruk
                </button>
              ) : null}
              <button type="submit" className="mh-btn-primary" disabled={kuyrukSaving}>
                <i className={`fas fa-${kuyrukSaving ? 'spinner fa-spin' : 'save'}`} aria-hidden />
                {kuyrukSaving ? ' Kaydediliyor…' : kuyrukEditingId ? ' Güncelle' : ' Ekle'}
              </button>
            </div>
          </form>

          {kuyrukYukleniyor ? (
            <div className="mh-loading"><i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…</div>
          ) : (
            <div className="mh-table-wrap">
              <table className="admins-table mh-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Kuyruk Adı</th>
                    <th>Açıklama</th>
                    <th>Üstlenebilir</th>
                    <th>Aktif</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {kuyruklar.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="mh-table-empty">
                        Henüz kuyruk tanımlanmamış. OPERATOR ve YONETIM kuyruklarını ekleyin.
                      </td>
                    </tr>
                  ) : (
                    kuyruklar.map((k) => (
                      <tr key={k.id || k._id}>
                        <td><strong>{k.numara}</strong></td>
                        <td>{k.ad}</td>
                        <td>{k.aciklama || '—'}</td>
                        <td>{k.ustlenebilir !== false ? 'Evet' : 'Hayır'}</td>
                        <td>{k.aktif !== false ? 'Evet' : 'Hayır'}</td>
                        <td className="table-actions islemler-sutunu">
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => kuyrukDuzenleBasla(k)}
                            title="Düzenle"
                          >
                            <i className="fas fa-edit" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() => kuyrukSilme(k)}
                            title="Sil"
                          >
                            <i className="fas fa-trash-alt" aria-hidden />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
